import { isNotEqual } from 'vet-bones/utils';

import { ModelConceptAttribute } from 'src/containers/ProjectDiagram/types/data';
import { DiagramSnapshot } from 'src/containers/ProjectDiagram/types/diagram';
import { getIsSensitivePropertyGroupNameModified } from 'src/containers/ProjectDiagram/utils/draft/draftSensitivePropertyGroup';

/**
 * Returns a set of class IRIs that were added to a domain of an attribute in preview mode.
 *
 * @returns {Set<string>} - Set of class IRIs
 */
export const getAddedAttributeDomains = ({
  currentAttribute,
  previousAttribute,
  isNewAttribute,
}: {
  currentAttribute: ModelConceptAttribute | undefined;
  previousAttribute: ModelConceptAttribute | undefined;
  isNewAttribute: boolean;
}) => {
  const addedDomains = new Set(Object.keys(currentAttribute?.domains || {}));

  if (!isNewAttribute) {
    Object.keys(previousAttribute?.domains || {}).forEach((attributeIri) => {
      addedDomains.delete(attributeIri);
    });
  }

  return addedDomains;
};

export const getIsAttributeDatatypeModified = ({
  currentAttribute,
  previousAttribute,
  isNewAttribute,
}: {
  currentAttribute: ModelConceptAttribute | undefined;
  previousAttribute: ModelConceptAttribute | undefined;
  isNewAttribute: boolean;
}) => {
  const currentRanges = Object.keys(currentAttribute?.ranges || {});
  const previousRanges = Object.keys(previousAttribute?.ranges || {});
  return (
    (isNewAttribute && currentRanges.length) ||
    isNotEqual(currentRanges.sort(), previousRanges.sort())
  );
};

/**
 * Returns a set of SPG names that had a given attribute added/removed in preview mode.
 *
 * @returns {Set<string>} - Set of SPG names
 */
export const getModifiedAttributeSpgs = ({
  attributeIri,
  isNewAttribute,
  diagram,
}: {
  attributeIri: string;
  isNewAttribute: boolean;
  diagram: DiagramSnapshot;
}) => {
  const modifiedSpgs = new Set<string>();

  const previousSpgs = new Set<string>();
  Object.entries(
    diagram.model.modelData.draftData?.prevModelData
      .sensitivePropertyGroupsByName || {}
  ).forEach(([spgName, spg]) => {
    if (spg.includes(attributeIri)) {
      previousSpgs.add(spgName);
    }
  });
  Object.entries(diagram.model.modelData.sensitivePropertyGroupsByName).forEach(
    ([spgName, spg]) => {
      if (!spg.includes(attributeIri)) {
        return;
      }

      const originalSpgName =
        diagram.model.modelData.draftData?.originalSpgNamesByCurrentName?.[
          spgName
        ];
      const previousSpg =
        diagram.model.modelData.draftData?.prevModelData
          .sensitivePropertyGroupsByName[originalSpgName || spgName];
      const isNewSpg = !previousSpg;

      const isSensitivePropertyGroupNameModified =
        getIsSensitivePropertyGroupNameModified({
          originalSpgName,
          isNewSpg,
        });

      if (
        isNewAttribute ||
        isNewSpg ||
        isSensitivePropertyGroupNameModified ||
        !previousSpgs.has(originalSpgName || spgName)
      ) {
        modifiedSpgs.add(spgName);
      }
    }
  );

  return modifiedSpgs;
};
